import React, {useCallback} from 'react';
import {useNavigate} from 'react-router-dom';

import {BUTTON_TYPE, RemoveChangeRequest} from 'src/components/remove-change-request/remove-change-request';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {useUserRoles} from 'src/hooks/useUserRoles';
import {ChangeRequestReviewController, useAiUsage} from 'src/modules/ai';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {createMainTabbRoutePath} from 'src/modules/tabs-panel/tabs-route-planner';
import {TabId} from 'src/modules/tabs-panel/typedefs';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {Role} from 'src/openapi/models/Role';
import {useAppSelector} from 'src/store';

import Button from '@pb/kit-component-button';
import Group from '@pb/kit-component-group';
import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';

type Props = {
    changeRequestId: number | null,
    status: ChangeRequestStatus,
    onStartReview: () => void,
    onStopReview: () => void,
    onSendToApprove: () => void,
    onApprove: () => void,
    onReopen: (openUpdateForm: boolean) => void,
    onReject: () => void,
    onShowUserFeedbackForm: () => void,
};

export const EventReviewActionBar = (props: Props) => {
    const {
        changeRequestId,
        status,
        onStartReview,
        onStopReview,
        onSendToApprove,
        onApprove,
        onReopen,
        onReject,
        onShowUserFeedbackForm,
    } = props;
    const changeRequestInfo = useAppSelector(getChangeRequestInfo)?.[changeRequestId || 0];
    const userRoles = useUserRoles();
    const userEmail = useUserEmail();
    const navigate = useNavigate();
    const {checkFeedbackModal} = useAiUsage(changeRequestId || 0);
    const isAiUsed = checkFeedbackModal();

    const hasAuthorAccessForReopening = status === ChangeRequestStatus.WAITING_REVIEW &&
        changeRequestInfo.authorEmail === userEmail;

    const canBeApproved = status === ChangeRequestStatus.WAITING_APPROVE &&
        userRoles.includes(Role.ROLE_UDEDT_APPROVER);

    const inReview = status === ChangeRequestStatus.WAITING_REVIEW ||
        status === ChangeRequestStatus.IN_REVIEW;

    const canBeReviewed = inReview && userRoles.includes(Role.ROLE_UDEDT_REVIEWER);

    const canBeRemoved = status !== ChangeRequestStatus.APPROVED &&
        (userRoles.includes(Role.ROLE_UDEDT_ADMIN) || changeRequestInfo.authorEmail === userEmail);

    const canLeaveFeedback = isAiUsed && status !== ChangeRequestStatus.IN_REVIEW;

    const onOpenMainTab = useCallback(() => {
        const mainTabbRoutePath = createMainTabbRoutePath({pathName: TabId.CHANGE_REQUESTS});
        navigate(mainTabbRoutePath);
    }, [navigate]);

    return (
        <Group className="pb-xxl">
            {canBeApproved ? (
                <Group>
                    <Button onClick={onApprove} variant="Type.PRIMARY">Approve</Button>
                    <Button onClick={() => onReopen(false)} variant="Type.PRIMARY">Reopen</Button>
                    <Button onClick={onReject} variant="Type.PRIMARY">Reject</Button>
                </Group>
            ) : null}
            {canBeReviewed ? (
                <Group>
                    {ChangeRequestStatus.WAITING_REVIEW === status ? (
                        <Group>
                            <Button onClick={onStartReview} variant="Type.PRIMARY">Start Review</Button>
                        </Group>
                    ) : (
                        <Group>
                            <Button onClick={onStopReview} variant="Type.PRIMARY">Stop Review</Button>
                            <Button onClick={onSendToApprove} variant="Type.PRIMARY">Send to Approve</Button>
                            <Button onClick={() => onReopen(false)} variant="Type.PRIMARY">Reopen</Button>
                            <Button onClick={onReject} variant="Type.PRIMARY">Reject</Button>
                        </Group>
                    )}
                </Group>
            ) : null}
            {hasAuthorAccessForReopening ?
                    <Button onClick={() => onReopen(true)} variant="Type.PRIMARY">Reopen</Button> : null}
            {canBeRemoved ? (
                <PushNotification anchorOrigin={{
                    horizontal: HorizontalPositionElement.RIGHT,
                    vertical: VerticalPositionElement.BOTTOM,
                }}>
                    <RemoveChangeRequest
                        changeRequest={changeRequestInfo}
                        buttonType={BUTTON_TYPE.BUTTON}
                    />
                </PushNotification>
            ) : null}
            <Button onClick={onOpenMainTab} variant="Type.SECONDARY">
                Back to Change Requests
            </Button>
            {status === ChangeRequestStatus.IN_REVIEW ? (
                <ChangeRequestReviewController changeRequestId={changeRequestId}/>
            ) : null}
            {canLeaveFeedback ? (
                <Button variant="Type.SECONDARY" onClick={onShowUserFeedbackForm}>
                    Give AI Feedback
                </Button>
            ) : null}
        </Group>
    );
};
